import { memo, useMemo } from 'react';
import styled from 'foundations-theming/utils';
import { Select } from 'foundations-components/transitional/components/Select';
import { isOptionGroup } from 'foundations-components/transitional/types';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Small } from 'foundations-components/transitional/components/Small';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { CheckCircleIcon } from 'foundations-assets/icons/components/CheckCircleIcon';
import { UserIcon } from 'foundations-assets/icons/components/UserIcon';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { DealPlanBuyerTypename, DealPlanCompanyAssigneeTypename, DealPlanSellerTypename } from '../../../__generated__/chirp/com/hubspot/deal/plans/rpc/DealPlansRpc';
import { SellerCandidateTypename, BuyerCandidateTypename, CompanyCandidateTypename } from './types';
import { getMilestoneAssigneeName, formatContactDisplayName } from '../../utils/ownerUtils';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CompactSelectWrapper = styled.div.withConfig({
  displayName: "OwnerSelect__CompactSelectWrapper"
})(["display:inline-flex;"]);
const OwnerSelectButtonContent = ({
  owner,
  renderAvatar,
  showName = true
}) => {
  if (!owner) {
    if (!showName) {
      return /*#__PURE__*/_jsx(UserIcon, {
        size: "200",
        color: "icon-core-disabled"
      });
    }
    return /*#__PURE__*/_jsx(Text, {
      typography: "body-100",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "dealPlan.milestones.assignOwner"
      })
    });
  }
  return /*#__PURE__*/_jsxs(Flex, {
    alignItems: "center",
    gap: "300",
    overflow: "hidden",
    children: [renderAvatar === null || renderAvatar === void 0 ? void 0 : renderAvatar(owner), showName && /*#__PURE__*/_jsx(Text, {
      as: "span",
      typography: "body-100",
      children: /*#__PURE__*/_jsx(TruncateString, {
        children: getMilestoneAssigneeName(owner)
      })
    })]
  });
};
const OwnerOptionRenderer = /*#__PURE__*/memo(({
  option,
  currentValue,
  renderAvatar
}) => /*#__PURE__*/_jsxs(Flex, {
  alignItems: "center",
  gap: "300",
  overflow: "hidden",
  paddingInlineStart: isOptionGroup(option) ? '300' : '0',
  paddingBlock: "200",
  marginInlineStart: isOptionGroup(option) ? '0' : '-200',
  children: [/*#__PURE__*/_jsx(Flex, {
    inlineSize: "16px",
    children: currentValue !== undefined && option.value === currentValue && /*#__PURE__*/_jsx(CheckCircleIcon, {
      size: "200"
    })
  }), !isOptionGroup(option) && (renderAvatar === null || renderAvatar === void 0 ? void 0 : renderAvatar(option.owner)), /*#__PURE__*/_jsxs(View, {
    minInlineSize: "0",
    flex: "1",
    overflow: "hidden",
    children: [isOptionGroup(option) ? /*#__PURE__*/_jsx(TruncateString, {
      children: option.text
    }) : /*#__PURE__*/_jsx(Text, {
      as: "span",
      typography: "body-100",
      children: /*#__PURE__*/_jsx(TruncateString, {
        children: option.text
      })
    }), option.role && /*#__PURE__*/_jsx(Small, {
      use: "help",
      children: /*#__PURE__*/_jsx(Text, {
        as: "span",
        typography: "body-100",
        children: option.role
      })
    })]
  })]
}));
const getCurrentValue = owner => {
  if (!owner) return undefined;
  if (owner.__typename === DealPlanSellerTypename) {
    var _owner$ownerId;
    return `${(_owner$ownerId = owner.ownerId) !== null && _owner$ownerId !== void 0 ? _owner$ownerId : owner.userId}`;
  }
  if (owner.__typename === DealPlanBuyerTypename) {
    return `${owner.contactVid}`;
  }
  if (owner.__typename === DealPlanCompanyAssigneeTypename) {
    return `company-${owner.companyId}`;
  }
  return undefined;
};
export const OwnerSelect = ({
  candidates,
  currentOwner,
  onOwnerChange,
  sellerGroupName,
  renderAvatar,
  loading,
  showName = true
}) => {
  const sellerCandidates = useMemo(() => candidates.filter(c => c.__typename === SellerCandidateTypename), [candidates]);
  const buyerCandidates = useMemo(() => candidates.filter(c => c.__typename === BuyerCandidateTypename), [candidates]);
  const companyCandidates = useMemo(() => candidates.filter(c => c.__typename === CompanyCandidateTypename), [candidates]);
  const candidateOptions = useMemo(() => {
    if (candidates.length === 0) {
      return [];
    }
    const sellerOptions = [{
      text: sellerGroupName || I18n.text('dealPlan.milestones.groupSeller'),
      options: sellerCandidates.map(candidate => ({
        text: formatContactDisplayName(candidate),
        value: `${candidate.ownerId}`,
        role: candidate.role,
        owner: {
          userId: candidate.ownerId,
          ownerId: candidate.ownerId,
          firstName: candidate.firstName,
          lastName: candidate.lastName,
          __typename: DealPlanSellerTypename
        }
      }))
    }];
    const companyBuyerOptions = companyCandidates.map(candidate => {
      var _candidate$name;
      return {
        text: (_candidate$name = candidate.name) !== null && _candidate$name !== void 0 ? _candidate$name : '',
        value: `company-${candidate.companyId}`,
        owner: {
          companyId: candidate.companyId,
          name: candidate.name,
          __typename: DealPlanCompanyAssigneeTypename
        }
      };
    });
    const contactBuyerOptions = buyerCandidates.map(candidate => ({
      text: formatContactDisplayName(candidate),
      value: `${candidate.contactVid}`,
      role: candidate.role,
      owner: {
        contactVid: candidate.contactVid,
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        __typename: DealPlanBuyerTypename
      }
    }));
    const buyerOptions = [{
      text: companyCandidates.length === 1 && companyCandidates[0].name ? companyCandidates[0].name : I18n.text('dealPlan.milestones.groupBuyer'),
      options: [...companyBuyerOptions, ...contactBuyerOptions]
    }];
    return [...sellerOptions, ...buyerOptions];
  }, [candidates, sellerCandidates, buyerCandidates, companyCandidates, sellerGroupName]);
  const handleChange = arg => {
    const selectedId = arg.target.value;
    const seller = sellerCandidates.find(c => `${c.ownerId}` === selectedId);
    if (seller) {
      onOwnerChange({
        userId: seller.ownerId,
        ownerId: seller.ownerId,
        firstName: seller.firstName,
        lastName: seller.lastName,
        email: seller.email,
        __typename: DealPlanSellerTypename
      });
      return;
    }
    const buyer = buyerCandidates.find(c => `${c.contactVid}` === selectedId);
    if (buyer) {
      onOwnerChange({
        contactVid: buyer.contactVid,
        firstName: buyer.firstName,
        lastName: buyer.lastName,
        email: buyer.email,
        __typename: DealPlanBuyerTypename
      });
      return;
    }
    const company = companyCandidates.find(c => `company-${c.companyId}` === selectedId);
    if (company) {
      onOwnerChange({
        companyId: company.companyId,
        name: company.name,
        __typename: DealPlanCompanyAssigneeTypename
      });
    }
  };
  if (loading) {
    return null;
  }
  const currentValue = getCurrentValue(currentOwner);
  const select = /*#__PURE__*/_jsx(Select, {
    anchorType: "button",
    buttonUse: "unstyled",
    ButtonContent: OwnerSelectButtonContent,
    buttonContentProps: {
      owner: currentOwner,
      renderAvatar,
      showName
    },
    options: candidateOptions,
    value: currentValue,
    optionRenderer: option => /*#__PURE__*/_jsx(OwnerOptionRenderer, {
      option: option,
      currentValue: currentValue,
      renderAvatar: renderAvatar
    }),
    menuWidth: 240,
    onChange: handleChange
  });
  return showName ? select : /*#__PURE__*/_jsx(CompactSelectWrapper, {
    children: select
  });
};